import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { fileUrl } from '../services/api';
import { makeMoneyFormatter, getDisplayCurrency, convertAmount } from './currency';

// Colors (QuickBooks Desktop style: black + light gray)
const PRIMARY = [0, 0, 0];        // Black
const ACCENT = [37, 99, 235];    // Blue
const GREEN = [22, 163, 74];
const RED = [220, 38, 38];
const GRAY = [100, 116, 139];
const LIGHT_GRAY = [241, 245, 249];
const HEAD_FILL = [243, 244, 246];
const BORDER = [200, 204, 210];
const LINE = [229, 231, 235];

/**
 * Read a CSS variable from :root and convert to [r,g,b].
 * Falls back to the provided default if not set.
 */
function getDocAccent(docType) {
  try {
    const varName = `--doc-${docType}`;
    const raw = getComputedStyle(document.documentElement).getPropertyValue(varName).trim();
    if (raw && raw.startsWith('#')) {
      const r = parseInt(raw.slice(1, 3), 16);
      const g = parseInt(raw.slice(3, 5), 16);
      const b = parseInt(raw.slice(5, 7), 16);
      return [r, g, b];
    }
  } catch (e) { /* fallback */ }
  return ACCENT;
}

/**
 * Fetch company logo and convert to base64 data URL for jsPDF.
 * Returns null if no logo or fetch fails.
 */
async function fetchLogoBase64(logoPath) {
  if (!logoPath) return null;
  try {
    const url = fileUrl(logoPath);
    const response = await fetch(url);
    if (!response.ok) return null;
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch (e) {
    return null;
  }
}

function hexToRGB(hex) {
  if (!hex || !hex.startsWith('#')) return ACCENT;
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return [r, g, b];
}

/**
 * Build a totals-row entry showing the doc-currency total converted to the
 * company base currency. Returns [] when unnecessary (same currency, no rate, or no total).
 */
function fxTotalsRow(currency, baseCurrency, rate, total, fmtBase) {
  if (!currency || currency === baseCurrency || !parseFloat(rate) || !parseFloat(total)) return [];
  return [['≈ in ' + baseCurrency, fmtBase(convertAmount(total, rate)), false, false]];
}

function getDocPrimary() {
  try {
    const raw = getComputedStyle(document.documentElement).getPropertyValue('--brand-primary').trim();
    if (raw && raw.startsWith('#')) {
      const r = parseInt(raw.slice(1, 3), 16);
      const g = parseInt(raw.slice(3, 5), 16);
      const b = parseInt(raw.slice(5, 7), 16);
      return [r, g, b];
    }
  } catch (e) { /* fallback */ }
  return PRIMARY;
}

/**
 * Read a CSS border variable from :root and parse it into { style, color }.
 */
function getDocBorderStyle(docType) {
  try {
    const raw = getComputedStyle(document.documentElement).getPropertyValue(`--doc-${docType}-border`).trim();
    if (!raw || raw === 'none') return { style: 'none', color: null };
    // Parse '2px dashed #2563EB' → { style: 'dashed', color: '#2563EB' }
    const parts = raw.split(' ');
    const style = parts[1] || 'solid';
    const color = parts[2] || null;
    return { style, color };
  } catch (e) { return { style: 'none', color: null }; }
}

/**
 * Draw the company letterhead at the top of the PDF.
 * Returns the Y position after the letterhead.
 */
function drawLetterhead(doc, company, pageW, margin, accentColor, logoDataUrl) {
  const contentW = pageW - margin * 2;
  let y = margin;

  // Company name
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.setTextColor(...PRIMARY);
  doc.text(company?.name || 'LedgerPro', margin, y + 6);

  // Company details on the right
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...GRAY);
  const details = [];
  if (company?.email) details.push(company.email);
  if (company?.phone) details.push(`Phone: ${company.phone}`);
  if (company?.website) details.push(company.website);
  if (details.length) {
    doc.text(details.join('  ·  '), pageW - margin, y + 3, { align: 'right' });
  }

  // Address
  const address = [
    company?.address_line1,
    company?.address_line2,
    [company?.city, company?.state, company?.postal_code].filter(Boolean).join(', '),
    company?.country && company.country !== 'US' ? company.country : '',
  ].filter(Boolean).join(', ');
  if (address) {
    doc.setFontSize(8);
    doc.text(address, pageW - margin, y + 9, { align: 'right' });
  }

  // Logo
  if (logoDataUrl) {
    try {
      // Determine image format from data URL
      const fmt = logoDataUrl.includes('image/png') ? 'PNG' : 'JPEG';
      const imgW = 40;
      const imgH = 20;
      doc.addImage(logoDataUrl, fmt, margin, y - 6, imgW, imgH);
    } catch (e) { /* logo embed failed, skip */ }
  }

  y += 18;

  return y;
}

/**
 * Draw a styled border line under the header.
 * @param {string} style - 'none' | 'solid' | 'dashed' | 'double' | 'thick' | 'dotted'
 */
function drawHeaderBorder(doc, style, color, pageW, margin, y) {
  if (!style || style === 'none') return y;
  doc.setDrawColor(...(color || PRIMARY));
  switch (style) {
    case 'dashed':
      doc.setLineWidth(0.5);
      doc.setLineDashPattern([4, 3], 0);
      doc.line(margin, y, pageW - margin, y);
      doc.setLineDashPattern([], 0);
      break;
    case 'double':
      doc.setLineWidth(0.4);
      doc.line(margin, y - 1, pageW - margin, y - 1);
      doc.line(margin, y + 1, pageW - margin, y + 1);
      break;
    case 'thick':
      doc.setLineWidth(1.2);
      doc.line(margin, y, pageW - margin, y);
      break;
    case 'dotted':
      doc.setLineWidth(0.5);
      doc.setLineDashPattern([1, 2], 0);
      doc.line(margin, y, pageW - margin, y);
      doc.setLineDashPattern([], 0);
      break;
    case 'solid':
    default:
      doc.setLineWidth(0.6);
      doc.line(margin, y, pageW - margin, y);
      break;
  }
  return y + 6;
}

/**
 * Draw document title + meta block. Returns Y after the block.
 */
function drawDocHeader(doc, title, meta, pageW, margin, y, accentColor) {
  const halfW = (pageW - margin * 2) / 2;

  // Title
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(22);
  doc.setTextColor(...PRIMARY);
  doc.text(title, margin, y + 8);

  // Meta info on the right
  doc.setFontSize(9);
  let my = y + 2;
  (meta || []).forEach(([label, value]) => {
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...GRAY);
    doc.text(label, pageW - margin - halfW + 2, my);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...PRIMARY);
    doc.text(String(value || '—'), pageW - margin, my, { align: 'right' });
    my += 5;
  });

  return Math.max(y + 14, my) + 4;
}

/**
 * Draw party info (Bill To / Ship To). Returns Y after block.
 */
function drawPartyBlock(doc, label, party, pageW, margin, y) {
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(...GRAY);
  doc.text(label.toUpperCase(), margin, y);
  y += 5;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(...PRIMARY);
  doc.text(party?.name || '—', margin, y);
  y += 5;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...GRAY);
  if (party?.address) {
    doc.text(party.address, margin, y);
    y += 4;
  }
  if (party?.email) {
    doc.text(party.email, margin, y);
    y += 4;
  }
  if (party?.phone) {
    doc.text(party.phone, margin, y);
    y += 4;
  }

  return y + 4;
}

/**
 * Draw the line items table. Returns Y after the table.
 */
function drawItemsTable(doc, items, pageW, margin, y, fmt) {
  const columns = [
    { header: 'Description', dataKey: 'description' },
    { header: 'Qty', dataKey: 'quantity' },
    { header: 'Rate', dataKey: 'unit_price' },
    { header: 'Tax', dataKey: 'tax' },
    { header: 'Amount', dataKey: 'amount' },
  ];

  const rows = (items || []).map(item => {
    const name = item.product_name || item.item_name || '';
    const lineDesc = item.description && item.description !== name ? item.description : '';
    const desc = lineDesc || item.product_description || '';
    return {
      description: [name, desc].filter(Boolean).join('\n') || '—',
      quantity: String(item.quantity || 0),
      unit_price: fmt(item.unit_price),
      tax: fmt(item.tax_amount),
      amount: fmt(item.amount),
    };
  });

  if (rows.length === 0) {
    rows.push({ description: 'No line items', quantity: '', unit_price: '', tax: '', amount: '' });
  }

  doc.autoTable({
    startY: y,
    columns,
    body: rows,
    margin: { left: margin, right: margin },
    styles: { fontSize: 9, cellPadding: 4, textColor: [0, 0, 0] },
    headStyles: { fillColor: HEAD_FILL, textColor: [0, 0, 0], fontStyle: 'bold', fontSize: 8, lineColor: [0, 0, 0], lineWidth: 0.5 },
    alternateRowStyles: { fillColor: [255, 255, 255] },
    columnStyles: {
      description: { cellWidth: 'auto' },
      quantity: { halign: 'right', cellWidth: 22 },
      unit_price: { halign: 'right', cellWidth: 32 },
      tax: { halign: 'right', cellWidth: 28 },
      amount: { halign: 'right', cellWidth: 32, fontStyle: 'bold' },
    },
    theme: 'grid',
    tableLineColor: LINE,
    tableLineWidth: 0.15,
  });

  return doc.lastAutoTable.finalY + 4;
}

/**
 * Draw the totals block. Returns Y after block.
 */
function drawTotals(doc, totals, pageW, margin, y, fmt) {
  const totalsW = 75;
  const x = pageW - margin - totalsW;

  (totals || []).forEach(([label, value, isBold, isHighlight]) => {
    doc.setFont('helvetica', isBold ? 'bold' : 'normal');
    doc.setFontSize(isHighlight ? 11 : 9);

    if (isHighlight) {
      doc.setFillColor(...HEAD_FILL);
      doc.setDrawColor(...PRIMARY);
      doc.setLineWidth(0.5);
      doc.rect(x - 4, y - 4.5, totalsW + 8, 9, 'FD');
      doc.setTextColor(...PRIMARY);
    } else {
      doc.setTextColor(isBold ? 0 : GRAY[0], isBold ? 0 : GRAY[1], isBold ? 0 : GRAY[2]);
    }

    doc.text(label, x, y);
    doc.text(String(value), x + totalsW, y, { align: 'right' });
    y += isHighlight ? 10 : 6;
  });

  return y + 4;
}

/**
 * Draw the footer. Called at the end.
 */
function drawFooter(doc, companyName, type, number, pageW) {
  const totalPages = doc.internal.getNumberOfPages();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 14;

  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setDrawColor(...PRIMARY);
    doc.setLineWidth(0.6);
    doc.line(margin, pageH - 12, pageW - margin, pageH - 12);
    doc.setTextColor(...GRAY);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.text(`Thank you for your business! ${companyName || 'LedgerPro'}` + (type ? ` · ${type}` : '') + (number ? ` #${number}` : ''), margin, pageH - 7);
    doc.text(`Page ${i} of ${totalPages}`, pageW - margin, pageH - 7, { align: 'right' });
  }
}

/**
 * Check if we need a new page.
 */
function checkPageBreak(doc, y, margin, threshold = 250) {
  if (y > threshold) {
    doc.addPage();
    return margin;
  }
  return y;
}

// ═══════════════════════════════════════════════════════════════════
// PUBLIC API
// ═══════════════════════════════════════════════════════════════════

/**
 * Generate an Invoice PDF.
 * @param {Object} invoice - { invoice_number, invoice_date, due_date, payment_terms, status,
 *   customer_name, customer_email, customer_phone, customer_address,
 *   items: [{ description, quantity, unit_price, tax_rate, tax_amount, amount }],
 *   subtotal, tax_amount, discount_amount, shipping_amount, total, amount_paid, amount_due, memo }
 * @param {Object} company - company info from useCompany()
 */
export async function generateInvoicePDF(invoice, company) {
  const doc = new jsPDF('p', 'mm', 'a4');
  const pageW = doc.internal.pageSize.getWidth();
  const margin = 14;
  const accent = getDocAccent('invoice');
  const primary = getDocPrimary();
  const fmt = makeMoneyFormatter(invoice?.currency || getDisplayCurrency());
  const fmtBase = makeMoneyFormatter(company?.base_currency || getDisplayCurrency());
  const logoDataUrl = await fetchLogoBase64(company?.logo_path);
  let y = drawLetterhead(doc, company, pageW, margin, accent, logoDataUrl);

  // Header border
  const border = getDocBorderStyle('invoice');
  y = drawHeaderBorder(doc, border.style, border.color ? hexToRGB(border.color) : accent, pageW, margin, y);

  // Document header
  y = drawDocHeader(doc, 'INVOICE', [
    ['Invoice #', invoice.invoice_number || '—'],
    ['Date', invoice.invoice_date || '—'],
    ['Due Date', invoice.due_date || '—'],
    ['Terms', invoice.payment_terms || '—'],
    ...(invoice.currency ? [['Currency', invoice.currency]] : []),
    ['Status', invoice.status || '—'],
  ], pageW, margin, y, accent);

  // Bill To
  y = drawPartyBlock(doc, 'Bill To', {
    name: invoice.customer_name,
    address: invoice.customer_address,
    email: invoice.customer_email,
    phone: invoice.customer_phone,
  }, pageW, margin, y);

  // Amount Due (right side)
  const balanceDue = invoice.amount_due ?? (invoice.total - (invoice.amount_paid || 0));
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...GRAY);
  doc.text('AMOUNT DUE', pageW - margin - 50, y - 10);
  doc.setFontSize(16);
  doc.setTextColor(...RED);
  doc.text(fmt(balanceDue), pageW - margin, y - 4, { align: 'right' });

  // Line items table
  y = drawItemsTable(doc, invoice.items, pageW, margin, y, fmt);

  // Totals
  y = drawTotals(doc, [
    ['Subtotal', fmt(invoice.subtotal), false, false],
    ['Tax', fmt(invoice.tax_amount), false, false],
    ...(invoice.discount_amount ? [['Discount', `-${fmt(invoice.discount_amount)}`, false, false]] : []),
    ...(invoice.shipping_amount ? [['Shipping', fmt(invoice.shipping_amount), false, false]] : []),
    ['Total', fmt(invoice.total), true, true],
    ...fxTotalsRow(invoice.currency, company?.base_currency, invoice.exchange_rate, invoice.total, fmtBase),
    ...(invoice.amount_paid ? [['Paid', `-${fmt(invoice.amount_paid)}`, false, false]] : []),
    ...(balanceDue ? [['Balance Due', fmt(balanceDue), true, false]] : []),
  ], pageW, margin, y, fmt);

  // Notes
  if (invoice.memo) {
    y = checkPageBreak(doc, y + 4, margin, 240);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(...GRAY);
    doc.text('NOTES', margin, y);
    y += 5;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...PRIMARY);
    const lines = doc.splitTextToSize(invoice.memo, pageW - margin * 2);
    doc.text(lines, margin, y);
    y += lines.length * 4 + 6;
  }

  // Footer
  drawFooter(doc, company?.name, 'Invoice', invoice.invoice_number, pageW);

  // Save
  doc.save(`Invoice-${invoice.invoice_number || 'draft'}.pdf`);
}

/**
 * Generate a Bill PDF.
 */
export async function generateBillPDF(bill, company) {
  const doc = new jsPDF('p', 'mm', 'a4');
  const pageW = doc.internal.pageSize.getWidth();
  const margin = 14;
  const accent = getDocAccent('purchase-invoice');
  const fmt = makeMoneyFormatter(bill?.currency || getDisplayCurrency());
  const fmtBase = makeMoneyFormatter(company?.base_currency || getDisplayCurrency());
  const logoDataUrl = await fetchLogoBase64(company?.logo_path);
  let y = drawLetterhead(doc, company, pageW, margin, accent, logoDataUrl);

  // Header border
  const border = getDocBorderStyle('purchase-invoice');
  y = drawHeaderBorder(doc, border.style, border.color ? hexToRGB(border.color) : accent, pageW, margin, y);

  y = drawDocHeader(doc, 'BILL', [
    ['Bill #', bill.bill_number || '—'],
    ['Date', bill.bill_date || '—'],
    ['Due Date', bill.due_date || '—'],
    ...(bill.currency ? [['Currency', bill.currency]] : []),
    ['Status', bill.status || '—'],
  ], pageW, margin, y, accent);

  // Vendor
  y = drawPartyBlock(doc, 'Vendor', {
    name: bill.vendor_name,
    email: bill.vendor_email,
    phone: bill.vendor_phone,
  }, pageW, margin, y);

  // Amount Due
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...GRAY);
  doc.text('AMOUNT DUE', pageW - margin - 50, y - 10);
  doc.setFontSize(16);
  doc.setTextColor(...RED);
  doc.text(fmt(bill.amount_due ?? bill.total), pageW - margin, y - 4, { align: 'right' });

  y = drawItemsTable(doc, bill.items, pageW, margin, y, fmt);

  y = drawTotals(doc, [
    ['Subtotal', fmt(bill.subtotal), false, false],
    ['Tax', fmt(bill.tax_amount), false, false],
    ['Total', fmt(bill.total), true, true],
    ...fxTotalsRow(bill.currency, company?.base_currency, bill.exchange_rate, bill.total, fmtBase),
    ...(bill.amount_paid ? [['Paid', `-${fmt(bill.amount_paid)}`, false, false]] : []),
    ...(bill.amount_due ? [['Balance Due', fmt(bill.amount_due), true, false]] : []),
  ], pageW, margin, y, fmt);

  if (bill.memo || bill.notes) {
    y = checkPageBreak(doc, y + 4, margin, 240);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(...GRAY);
    doc.text('NOTES', margin, y);
    y += 5;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...PRIMARY);
    const lines = doc.splitTextToSize(bill.memo || bill.notes, pageW - margin * 2);
    doc.text(lines, margin, y);
  }

  drawFooter(doc, company?.name, 'Bill', bill.bill_number, pageW);
  doc.save(`Bill-${bill.bill_number || 'draft'}.pdf`);
}

/**
 * Generate an Order (Estimate / Sales Order / Purchase Order) PDF.
 */
export async function generateOrderPDF(title, data, company) {
  const doc = new jsPDF('p', 'mm', 'a4');
  const pageW = doc.internal.pageSize.getWidth();
  const margin = 14;
  const docType = (title || '').toLowerCase().includes('estimate') ? 'estimate'
    : (title || '').toLowerCase().includes('purchase') ? 'purchase-order'
    : 'sales-order';
  const accent = getDocAccent(docType);
  const fmt = makeMoneyFormatter(data?.currency || getDisplayCurrency());
  const fmtBase = makeMoneyFormatter(company?.base_currency || getDisplayCurrency());
  const logoDataUrl = await fetchLogoBase64(company?.logo_path);
  let y = drawLetterhead(doc, company, pageW, margin, accent, logoDataUrl);

  // Header border
  const border = getDocBorderStyle(docType);
  y = drawHeaderBorder(doc, border.style, border.color ? hexToRGB(border.color) : accent, pageW, margin, y);

  y = drawDocHeader(doc, (title || 'ORDER').toUpperCase(), [
    ['Number', data.number || '—'],
    ['Date', data.date || '—'],
    ...(data.secondLabel && data.second ? [[data.secondLabel, data.second]] : []),
    ...(data.currency ? [['Currency', data.currency]] : []),
    ['Status', data.status || '—'],
  ], pageW, margin, y, accent);

  y = drawPartyBlock(doc, data.partyLabel || 'Party', {
    name: data.party_name,
    address: data.party_address,
    email: data.party_email,
    phone: data.party_phone,
  }, pageW, margin, y);

  y = drawItemsTable(doc, data.items, pageW, margin, y, fmt);

  y = drawTotals(doc, [
    ['Subtotal', fmt(data.subtotal), false, false],
    ['Tax', fmt(data.tax_amount), false, false],
    ...(data.discount_amount ? [['Discount', `-${fmt(data.discount_amount)}`, false, false]] : []),
    ['Total', fmt(data.total), true, true],
    ...fxTotalsRow(data.currency, company?.base_currency, data.exchange_rate, data.total, fmtBase),
  ], pageW, margin, y, fmt);

  if (data.notes) {
    y = checkPageBreak(doc, y + 4, margin, 240);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(...GRAY);
    doc.text('NOTES', margin, y);
    y += 5;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...PRIMARY);
    const lines = doc.splitTextToSize(data.notes, pageW - margin * 2);
    doc.text(lines, margin, y);
  }

  drawFooter(doc, company?.name, title, data.number, pageW);
  doc.save(`${(title || 'Order').replace(/\s+/g, '-')}-${data.number || 'draft'}.pdf`);
}

/**
 * Generate a Pay Slip PDF.
 */
export async function generatePaySlipPDF(employee, payslipData, company) {
  const doc = new jsPDF('p', 'mm', 'a4');
  const pageW = doc.internal.pageSize.getWidth();
  const margin = 14;
  const fmt = makeMoneyFormatter(getDisplayCurrency());
  const logoDataUrl = await fetchLogoBase64(company?.logo_path);
  let y = drawLetterhead(doc, company, pageW, margin, ACCENT, logoDataUrl);

  y = drawDocHeader(doc, 'EMPLOYEE PAY SLIP', [
    ['Employee #', employee.employee_number || '—'],
    ['Pay Period', payslipData.payPeriod || '—'],
    ['Pay Frequency', employee.pay_frequency || '—'],
  ], pageW, margin, y);

  // Employee Info
  doc.setFillColor(...LIGHT_GRAY);
  doc.roundedRect(margin, y, pageW - margin * 2, 20, 2, 2, 'F');
  doc.setDrawColor(...BORDER);
  doc.roundedRect(margin, y, pageW - margin * 2, 20, 2, 2, 'S');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...PRIMARY);
  doc.text(`${employee.first_name} ${employee.last_name}`, margin + 6, y + 7);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...GRAY);
  doc.text(`Title: ${employee.job_title || '—'}`, margin + 6, y + 13);
  doc.text(`Department: ${employee.department || '—'}`, margin + 6, y + 18);
  doc.text(`Annual Salary: ${fmt(employee.salary)}`, margin + 90, y + 13);
  doc.text(`Employee #: ${employee.employee_number}`, margin + 90, y + 18);
  y += 26;

  // Earnings & Deductions table
  const earnings = payslipData.grossPay || 0;
  const federalTax = payslipData.federalTax || 0;
  const fica = payslipData.fica || 0;
  const totalDeductions = federalTax + fica;
  const netPay = payslipData.netPay || 0;

  doc.autoTable({
    startY: y,
    columns: [
      { header: 'Earnings', dataKey: 'desc' },
      { header: 'Amount', dataKey: 'amount' },
      { header: 'Deductions', dataKey: 'dedesc' },
      { header: 'Amount', dataKey: 'deamt' },
    ],
    body: [
      { desc: 'Base Salary', amount: fmt(earnings), dedesc: `Federal Tax (est. ${(payslipData.taxRate || 22)}%)`, deamt: fmt(federalTax) },
      { desc: 'Gross Pay', amount: fmt(earnings), dedesc: 'FICA — SS & Medicare (7.65%)', deamt: fmt(fica) },
      { desc: '', amount: '', dedesc: 'Total Deductions', deamt: fmt(totalDeductions) },
    ],
    margin: { left: margin, right: margin },
    styles: { fontSize: 9, cellPadding: 4, textColor: [30, 41, 59] },
    headStyles: { fillColor: PRIMARY, fontStyle: 'bold', fontSize: 9 },
    alternateRowStyles: { fillColor: LIGHT_GRAY },
    columnStyles: {
      desc: { cellWidth: 55 },
      amount: { halign: 'right', cellWidth: 35, fontStyle: 'bold' },
      dedesc: { cellWidth: 55 },
      deamt: { halign: 'right', cellWidth: 35, fontStyle: 'bold' },
    },
    theme: 'grid',
    tableLineColor: BORDER,
  });
  y = doc.lastAutoTable.finalY + 6;

  // Net Pay highlight box
  doc.setFillColor(...PRIMARY);
  doc.roundedRect(margin, y, pageW - margin * 2, 14, 2, 2, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.setTextColor(255, 255, 255);
  doc.text('NET PAY', margin + 8, y + 9.5);
  doc.text(fmt(netPay), pageW - margin - 8, y + 9.5, { align: 'right' });
  y += 20;

  // Disclaimer
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(8);
  doc.setTextColor(...GRAY);
  doc.text('This is a computer-generated pay slip. Taxes shown are estimated. Consult your tax advisor for actual withholding amounts.', margin, y);

  drawFooter(doc, company?.name, 'Pay Slip', employee.employee_number, pageW);
  doc.save(`PaySlip-${employee.first_name}-${employee.last_name}.pdf`);
}
